import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Tooltip,
  InputAdornment,
  OutlinedInput,
  IconButton,
  FormHelperText,
} from "@material-ui/core";
import CalendarPicker from "../../../components/portico/calendar-picker/calendar-picker";
import { useStyles } from "./styles";
import { useDispatch } from "react-redux";
import { setSnackBar } from "../../../stores/ui/ui-action";
import { updateCompanyDetails } from "../../../services/bff-service";
import { fetchCompanyDetails } from "../../../stores/async/async-actions";
import CONSTANTS from "../../../utils/constants";
import { Parser } from "html-to-react";
import { useUser } from "../../../stores/user/user.hooks";
import { UserState } from "../../../stores/user/user-state";
import { InfoOutlined, Visibility, VisibilityOff } from "@material-ui/icons";

interface CompanyDetails {
  codatCompanyId: string;
  userEmail: string;
  userEntityMappingId: string;
  companyName: string;
  monthlyRefresh: boolean;
  connection_source: string;
  cif: string;
  teamCode: string;
  fyEndDate: string;
  primary_client_email: string;
  secondary_client_email: string;
  is_monthly_email_reminder_enabled: boolean;
  crm_entity_id: string;
  dbPlatform: string;
}

export const EditCompanyModal = ({ companyDetails, isCompanyDialogOpen, onCloseModal }) => {
  const styles = useStyles();

  const user = useUser() as UserState;
  const [companyInfo, setCompanyInfo] = useState<CompanyDetails>({} as CompanyDetails);

  useEffect(() => {
    const {
      user_email = "pgsupport@email.com",
      team_code = "",
      legal_entity_name: legalName = "",
      monthly_refresh: monthlyRefresh = false,
      connection_source = "dla",
      cif = "",
      fiscal_year_end_dt = "",
      primary_client_email = "",
      secondary_client_email = "",
      is_monthly_email_reminder_enabled = false,
      db_platform: dbPlatform,
      user_entity_mapping_id,
      codat_company_id,
      company_id,
      crm_entity_id,
    } = companyDetails;
    const initialFYEndDate = toLastDayOfMonth(new Date(fiscal_year_end_dt));

    const initialState = {
      codatCompanyId: codat_company_id,
      userEmail: user_email ?? "",
      userEntityMappingId: user_entity_mapping_id ?? company_id,
      companyName: legalName,
      monthlyRefresh: monthlyRefresh ?? false,
      connection_source: connection_source ?? "dla",
      cif,
      teamCode: team_code ?? "",
      fyEndDate: initialFYEndDate ?? "",
      primary_client_email: primary_client_email ?? "",
      secondary_client_email: secondary_client_email ?? "",
      is_monthly_email_reminder_enabled: is_monthly_email_reminder_enabled ?? false,
      dbPlatform: dbPlatform ?? "",
      crm_entity_id: crm_entity_id ?? null,
    };
    setCompanyInfo(initialState);
  }, [companyDetails]);

  const dispatch = useDispatch();
  const inputFormat = "MM/yyyy";

  const handleChange = ({ target: { name, value, checked } }: ChangeEvent<HTMLInputElement>) => {
    switch (name) {
      case "monthlyRefresh":
        setCompanyInfo((prevValue) => ({ ...prevValue, [name]: checked }));
        return;
      case "is_monthly_email_reminder_enabled":
        setCompanyInfo((prevValue) => {
          if (prevValue["primary_client_email"] || prevValue["secondary_client_email"])
            return { ...prevValue, [name]: checked };
          return prevValue;
        });
        return;
      case "primary_client_email":
        setCompanyInfo((prevValue) => {
          if (value || prevValue["secondary_client_email"]) return { ...prevValue, [name]: value };
          return { ...prevValue, [name]: value, is_monthly_email_reminder_enabled: false };
        });
        return;
      case "secondary_client_email":
        setCompanyInfo((prevValue) => {
          if (value || prevValue["primary_client_email"]) return { ...prevValue, [name]: value };
          return { ...prevValue, [name]: value, is_monthly_email_reminder_enabled: false };
        });
        return;
      default:
        setCompanyInfo((prevValue) => ({ ...prevValue, [name]: value }));
        return;
    }
  };

  const handleSubmit = async () => {
    const payload = {
      ...companyInfo,
    };

    try {
      const response = await updateCompanyDetails(payload);

      if (response?.status == true) {
        dispatch(
          setSnackBar({
            show: true,
            message: "Success",
            description: response?.message,
            messageType: "success",
          })
        );
        dispatch(fetchCompanyDetails(companyDetails.codat_company_id));
        onCloseModal();
      }
    } catch (e) {
      dispatch(
        setSnackBar({
          show: true,
          message: "Error",
          description: e?.response?.data?.error?.message ?? e.message,
          messageType: "error",
        })
      );
    }
  };

  const isInValidCif = (cif: string): boolean => {
    // returns 'true' ('invalid') if the CIF length is not 0 or not 9.
    return !/^(\d{9}|)$/.test(cif ?? "");
  };

  /**
   *  Validation againest the CRM Entity ID.
   *  Valid CRM Entity ID Values =  ["", null, [1-9], None]
   *  returns 'true' when the source value is not met with the target values.
   * */
  const isInValidCRMEntityID = (CRMEntityID: string): boolean => {
    return !/^(\d{0,10})$/.test(CRMEntityID ?? "");
  };

  /**
   *
   * It used to get the CRM Entity ID field status to enable or disable.
   * A valid numaric `CRM Entity ID` length should `1  - 10`.
   * - Admin has rights to edit CRM Entity ID always.
   * - CA_User, CS_User and CS_Cards is only be able to edit the field when the CRM Entity ID is invalid.
   * - Connection Source is "Manual" then the field status should be always editable.
   *
   */
  const getCRMFieldStatus = (CRMEntityID: string): boolean => {
    if (user?.user_role === "admin") return false;
    if (["credit_solutions", "credit_analysis", 'cs_cards'].includes(user?.user_role)) return /^(\d{1,10})$/.test(CRMEntityID);
    return true;
  };

  const handleOnEndDateChange = (e: any) => {
    let lastDateOfMonth = toLastDayOfMonth(e);
    setCompanyInfo((prevValue) => ({ ...prevValue, fyEndDate: lastDateOfMonth }));
  };

  function toLastDayOfMonth(date) {
    return new Date(date?.getFullYear(), date?.getMonth() + 1, 0)?.toLocaleDateString("sv-SE");
  }

  const getFormStatus = (): boolean => {
    const { companyName, fyEndDate, cif, crm_entity_id } = companyInfo;
    return !companyName || !fyEndDate || isInValidCif(cif) || isInValidCRMEntityID(crm_entity_id);
  };

  const isMonthlyRemainderDisabled = () => !companyInfo.primary_client_email && !companyInfo.secondary_client_email;

  //We use this variable as a workaround to display the Monthly Refresh Toggle as enabled because directly assigning companyInfo.monthlyRefresh did not show it as enabled
  const isMonthlyRefreshEnabled = companyInfo.monthlyRefresh  == true

  return (
    <Dialog
      open={isCompanyDialogOpen}
      className={styles.paper}
      disableScrollLock
      maxWidth="xl"
      data-testid="edit-company-modal"
    >
      <div className={styles.wrapper}>
        <DialogTitle className={styles.companyHeading}>
          <Typography>Edit Company Info</Typography>
        </DialogTitle>
        <DialogContent className={styles.formWrapper}>
          <div className={styles.gridWrapper}>
            <TextField
              id="companyName"
              name="companyName"
              label="Company Legal Name"
              inputProps={{ "data-testid": "companyName" }}
              variant="outlined"
              required={true}
              value={companyInfo.companyName}
              fullWidth
              onChange={handleChange}
            />
            <div className={styles.sectionWrapper}>
              <div>
                <div className={styles.switchControlWrapper}>
                  <Typography>Monthly Refresh</Typography>
                  <div>
                    <Switch
                      color="primary"
                      name="monthlyRefresh"
                      checked={isMonthlyRefreshEnabled}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <FormControl variant="outlined">
                  <InputLabel id="connection-source-label">Connection Source</InputLabel>
                  <Select
                    labelId="connection-source-label"
                    id="connection-source"
                    label="Connection Source"
                    value={companyInfo?.connection_source || "Manual"}
                    readOnly
                    fullWidth
                  >
                    <MenuItem value="dla">DLA</MenuItem>
                    <MenuItem value="i2c">ADF</MenuItem>
                    <MenuItem value="manual">Manual</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div>
                <TextField
                  label="Codat ID"
                  variant="outlined"
                  placeholder="Codat ID"
                  value={companyInfo.codatCompanyId}
                  required={true}
                  disabled
                  error={companyInfo.codatCompanyId === ""}
                  fullWidth
                />

                <TextField
                  id="cif"
                  label="CIF"
                  type="number"
                  inputProps={{ "data-testid": "cif" }}
                  name="cif"
                  value={companyInfo.cif}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  error={isInValidCif(companyInfo.cif)}
                  helperText={isInValidCif(companyInfo.cif) ? "CIF must be 9 digits" : ""}
                />
              </div>
              <div>
                <TextField
                  id="crmEntityID"
                  label="CRM Entity ID"
                  InputProps={{
                    endAdornment: getCRMFieldStatus(companyDetails?.crm_entity_id) ? (
                      <Tooltip title={Parser().parse(CONSTANTS.TOOLTIP_TITLES.CRM_ENTITY_ID)} placement="bottom" arrow>
                        <InfoOutlined fontSize="small" color="action" />
                      </Tooltip>
                    ) : (
                      <></>
                    ),
                  }}
                  inputProps={{
                    "data-testid": "crmEntityID",
                  }}
                  variant="outlined"
                  name="crm_entity_id"
                  value={companyInfo?.crm_entity_id}
                  onChange={handleChange}
                  disabled={getCRMFieldStatus(companyDetails?.crm_entity_id)}
                  fullWidth
                  error={isInValidCRMEntityID(companyInfo?.crm_entity_id)}
                  helperText={
                    isInValidCRMEntityID(companyInfo?.crm_entity_id)
                      ? "CRM entity ID must be 10 characters or fewer"
                      : ""
                  }
                />
                <TextField
                  id="teamCode"
                  label="Team Code"
                  inputProps={{ "data-testid": "teamCode" }}
                  variant="outlined"
                  name="teamCode"
                  value={companyInfo?.teamCode}
                  onChange={handleChange}
                  fullWidth
                />
              </div>
              <div>
                <CalendarPicker
                  name="fyEndDate"
                  label="FY End Date"
                  required={true}
                  inputFormat={inputFormat}
                  views={inputFormat === "MM/yyyy" ? ["year", "month"] : ["year", "month", "day"]}
                  setValue={() => {}}
                  value={companyInfo.fyEndDate ?? "-"}
                  onChange={handleOnEndDateChange}
                />
                <div className={styles.switchControlWrapper}>
                  <Typography className={styles.tooltipIconWrapper}>
                    Monthly Reminder Email{" "}
                    <Tooltip
                      title={Parser().parse(CONSTANTS.TOOLTIP_TITLES.MONTHLY_REMINDER_EMAIL)}
                      placement="top"
                      arrow
                    >
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                  </Typography>
                  <div>
                    <Tooltip
                      title={isMonthlyRemainderDisabled() ? CONSTANTS.TOOLTIP_TITLES.MONTHLY_REMINDER_EMAIL_STATUS : ""}
                      placement="top"
                      arrow
                    >
                      <span>
                        <Switch
                          data-testid="monthlyEmailReminder"
                          color="primary"
                          name="is_monthly_email_reminder_enabled"
                          checked={companyInfo.is_monthly_email_reminder_enabled}
                          onChange={handleChange}
                          disabled={isMonthlyRemainderDisabled()}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div>
                <TextField
                  id="primaryClientEmail"
                  label="Primary Client Email"
                  inputProps={{ "data-testid": "primaryClientEmail" }}
                  variant="outlined"
                  name="primary_client_email"
                  value={companyInfo.primary_client_email}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  id="secondaryClientEmail"
                  label="Secondary Client Email"
                  inputProps={{ "data-testid": "secondaryClientEmail" }}
                  variant="outlined"
                  value={companyInfo.secondary_client_email}
                  name="secondary_client_email"
                  onChange={handleChange}
                  fullWidth
                />
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions className={styles.actionSection}>
          <Button variant="text" className={styles.cancel} onClick={onCloseModal} color="primary">
            CANCEL
          </Button>
          <Button
            variant="text"
            className="default"
            color="primary"
            onClick={handleSubmit}
            data-testid="save-company"
            disabled={getFormStatus()}
          >
            SAVE CHANGES
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};
